import React, { useCallback, useEffect, useState } from 'react';

import { Plugins } from '@capacitor/core';
import { SensorData } from '@unicorns-and-unicorns/capacitor-sensors-v2';
import { useNavigate } from 'react-router-dom';

import Modal from 'components/modal';
import { useAuthContext } from 'state/auth';
import { CustomEventNames, EventTypes, useMParticleContext } from 'state/mParticle';
import LocalStorage from 'utils/cognito/storage';
import { StorageKeys } from 'utils/local-storage';
import { routes } from 'utils/routing';

export interface MappedSensorData {
  magnetometer: SensorData | undefined;
  gyroscope: SensorData | undefined;
  accelerometer: SensorData | undefined;
}

interface IWebViewWidgetProps {
  url: string;
}

export const WebviewUnicornsGhosts: React.FC<IWebViewWidgetProps> = ({ url }) => {
  const auth = useAuthContext();
  const mParticle = useMParticleContext();
  const navigate = useNavigate();

  const [frameRef, setFrameRef] = useState<HTMLIFrameElement | null>(null);
  const [magnetometer, setMagnetometer] = useState<SensorData>();
  const [gyroscope, setGyroscope] = useState<SensorData>();
  const [accelerometer, setAccelerometer] = useState<SensorData>();
  const userId = auth.user?.cognitoId || LocalStorage?.getItem(StorageKeys.USER)?.cognitoId;
  const uri = `${url}&uid=${userId}`;

  const handleOnMessage = (event: MessageEvent) => {
    const message = event.data.value ?? '';

    if (
      message === 'GHOST_FOUND' ||
      message === 'GHOST_NOT_FOUND' ||
      message === 'CLOSE_GHOST_DETECTOR'
    ) {
      mParticle.logEvent(CustomEventNames.CLOSE_GHOST_DETECTOR, EventTypes.Other);
      navigate(routes.base);
    }
  };

  useEffect(() => {
    window.addEventListener('message', handleOnMessage);

    const magnetometerListener = Plugins.Sensors.addListener(
      'magnetometerChange',
      (res: SensorData) => {
        setMagnetometer({
          x: res.x,
          y: res.y,
          z: res.z,
        });
      }
    );

    const gyroscopeListener = Plugins.Sensors.addListener('gyroscopeChange', (res: SensorData) => {
      setGyroscope({
        x: res.x,
        y: res.y,
        z: res.z,
      });
    });

    const accelerometerListener = Plugins.Sensors.addListener(
      'accelerometerChange',
      (res: SensorData) => {
        setAccelerometer({
          x: res.x,
          y: res.y,
          z: res.z,
        });
      }
    );

    return () => {
      window.removeEventListener('message', handleOnMessage);
      magnetometerListener.remove();
      gyroscopeListener.remove();
      accelerometerListener.remove();
    };
  }, []);

  useEffect(() => {
    postSensorData({
      magnetometer,
      gyroscope,
      accelerometer,
    });
  }, [magnetometer, gyroscope, accelerometer]);

  const postSensorData = (data: MappedSensorData) => {
    if (frameRef?.contentWindow) {
      frameRef.contentWindow.postMessage(
        {
          call: 'loadSensorData',
          value: data,
        },
        uri
      );
    }
  };

  const handleRef = useCallback(node => {
    setFrameRef(node);
  }, []);

  const closeModal = () => navigate(-1);

  return (
    <Modal
      onDismiss={closeModal}
      mParticleEventData={{
        modalAppearanceEventMessage: 'Home of the Ghosts Widget',
      }}
    >
      <iframe
        title="halloween"
        ref={handleRef}
        src={uri}
        frameBorder="0"
        style={{
          height: '90%',
          width: '100%',
          top: '10%',
          position: 'absolute',
          left: 0,
          right: 0,
        }}
        data-testid="webview-widget-ghosts"
      />
    </Modal>
  );
};
